import { Badge, Col, Row } from "react-bootstrap";

import {
  formatVisitorDate,
  formatVisitorDateOnly,
  formatVisitorValue,
  getVisitorMediaUrl,
  getVisitorProfileImageUrl,
} from "../../utils/visitors.utils";

const DetailItem = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{children ?? "-"}</div>
  </div>
);

const MediaCard = ({ label, path }) => {
  const url = getVisitorMediaUrl(path);

  return (
    <div className="mb-3">
      <div className="text-muted small mb-2">{label}</div>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={label}
            style={{
              width: "100%",
              maxHeight: "220px",
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          />
        </a>
      ) : (
        <div className="text-muted">No image uploaded</div>
      )}
    </div>
  );
};

const VisitorInfoSection = ({ visitor }) => {
  const profileUrl = getVisitorProfileImageUrl(visitor);
  const initials = String(visitor?.name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-4">
        {profileUrl ? (
          <img
            src={profileUrl}
            alt={visitor?.name || "Visitor"}
            style={{
              width: "72px",
              height: "72px",
              objectFit: "cover",
              borderRadius: "50%",
              border: "1px solid #dee2e6",
            }}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center bg-light text-muted"
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              border: "1px solid #dee2e6",
              fontSize: "20px",
              fontWeight: 600,
            }}
            aria-hidden="true"
          >
            {initials || "?"}
          </div>
        )}
        <div>
          <h5 className="mb-1">{visitor?.name || "Visitor"}</h5>
          <div className="text-muted mb-2">{visitor?.email || "-"}</div>
          <Badge bg={visitor?.is_verified ? "success" : "secondary"}>
            {visitor?.is_verified ? "Verified" : "Not verified"}
          </Badge>
        </div>
      </div>

      <div className="mb-4">
        <h5 className="mb-3">Personal Information</h5>
        <Row>
          <Col md={6}>
            <DetailItem label="Name">
              {formatVisitorValue(visitor?.name)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Email">
              {formatVisitorValue(visitor?.email)}
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="Gender">
              {formatVisitorValue(visitor?.gender)}
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="Age">
              {formatVisitorValue(visitor?.age)}
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="Date of Birth">
              {formatVisitorDateOnly(visitor?.date_of_birth)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Contact Number">
              {formatVisitorValue(visitor?.contact_number)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Occupation">
              {formatVisitorValue(visitor?.occupation)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Address">
              {formatVisitorValue(visitor?.address)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Location">
              {formatVisitorValue(visitor?.location)}
            </DetailItem>
          </Col>
        </Row>
      </div>

      <div className="mb-4">
        <h5 className="mb-3">Identity & License</h5>
        <Row>
          <Col md={4}>
            <DetailItem label="CNIC">
              {formatVisitorValue(visitor?.cnic)}
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="License Number">
              {formatVisitorValue(visitor?.license_number)}
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="License Expiry">
              {formatVisitorDateOnly(visitor?.license_expiry)}
            </DetailItem>
          </Col>
        </Row>
      </div>

      <div className="mb-4">
        <h5 className="mb-3">Account</h5>
        <Row>
          <Col md={4}>
            <DetailItem label="Role">
              <Badge bg="info">{visitor?.role || "visitor"}</Badge>
            </DetailItem>
          </Col>
          <Col md={4}>
            <DetailItem label="Verified">
              {visitor?.is_verified ? "Yes" : "No"}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Registered">
              {formatVisitorDate(visitor?.created_at || visitor?.createdAt)}
            </DetailItem>
          </Col>
          <Col md={6}>
            <DetailItem label="Last Updated">
              {formatVisitorDate(visitor?.updated_at || visitor?.updatedAt)}
            </DetailItem>
          </Col>
        </Row>
      </div>

      <div className="mb-2">
        <h5 className="mb-3">Documents</h5>
        <Row>
          <Col md={4}>
            <MediaCard label="Profile Image" path={visitor?.profile_image} />
          </Col>
          <Col md={4}>
            <MediaCard label="CNIC Image" path={visitor?.cnic_image} />
          </Col>
          <Col md={4}>
            <MediaCard label="License Image" path={visitor?.license_image} />
          </Col>
        </Row>
      </div>
    </>
  );
};

export default VisitorInfoSection;
